import { getCssVarObject, prefixObjectKeys } from '../utils';

import { colors } from './primitives';

const themeColorsDefault = {
  // Inks for fonts
  inkMain: colors['gray-05'],
  inkMedium: colors['gray-45'],
  inkLightMedium: colors['gray-70'],
  inkLight: colors['gray-90'],
  inkSuperLight: colors['gray-95'],

  // Basic background colors
  backgroundBase: colors['gray-95'],
  backgroundAlt: colors['gray-100'],

  // Transparent colors
  transparentMain5: colors['black-a-5'], // 5% alpha
  transparentMain10: colors['black-a-10'], // 10% alpha
  transparentMain15: colors['black-a-15'], // 15% alpha

  // Branding colors
  brandMain: colors['mint-85'],
  brandLight: colors['mint-95'],

  // Utility colors
  highlight: colors['indigo-35'],
  violet: colors['violet-60'],

  // Alert colors
  redMain: colors['red-45'],
  redMedium: colors['red-60'],
  redLight: colors['red-85'],

  // Selection colors
  blueMain: colors['blue-45'],
  blueMedium: colors['blue-60'],
  blueLight: colors['blue-a-30'],
  blueLight30: colors['blue-a-30'],

  greenMain: colors['green-55'],

  // Outdated, need to remove after migration to actual colors
  backgroundAltSolid: colors['gray-100'],
  brandMedium: colors['mint-85'],
  brandSuperLight: colors['mint-95'],
  transparentBlack: colors['black-a-10'],
  alertRed: colors['red-45'],
  alertRedLight: colors['red-85'],
  selectionBlue: colors['blue-60'],
  selectionBlue10: colors['blue-a-30'],
  selectionBlue30: colors['blue-a-30'],
  transparentBlack10: colors['black-a-10'], // 10% alpha
  transparentBlack15: colors['black-a-15'], // 15% alpha
} as const;

const themeColorsDark = {
  // Inks for fonts
  inkMain: colors['gray-95'],
  inkMedium: colors['gray-70'],
  inkLightMedium: colors['gray-55'],
  inkLight: colors['gray-25'],
  inkSuperLight: colors['gray-25'],

  // Basic background colors
  backgroundBase: colors['gray-05'],
  backgroundAlt: colors['gray-15'],

  // Transparent colors
  transparentMain5: colors['white-a-5'], // 5% alpha
  transparentMain10: colors['white-a-10'], // 10% alpha
  transparentMain15: colors['white-a-15'], // 15% alpha

  // Branding colors
  brandMain: colors['mint-85'],
  brandLight: colors['mint-95'],

  // Utility colors
  highlight: colors['indigo-43'],
  violet: colors['violet-50'],

  // Alert colors
  redMain: colors['red-85'],
  redMedium: colors['red-60'],
  redLight: colors['red-45'],

  // Selection colors
  blueMain: colors['blue-60'],
  blueMedium: colors['blue-45'],
  blueLight: colors['blue-a-30'],
  blueLight30: colors['blue-a-30'],

  greenMain: colors['green-55'],

  // Outdated, need to remove after migration to actual colors
  backgroundAltSolid: colors['gray-12'],
  brandMedium: colors['mint-85'],
  brandSuperLight: colors['mint-95'],
  transparentBlack: colors['white-a-10'],
  alertRed: colors['red-85'],
  alertRedLight: colors['red-45'],
  selectionBlue: colors['blue-45'],
  selectionBlue10: colors['blue-a-30'],
  selectionBlue30: colors['blue-a-30'],
  transparentBlack10: colors['white-a-10'], // 10% alpha
  transparentBlack15: colors['white-a-15'], // 15% alpha
} as const;

export const defaultTheme = {
  ...themeColorsDefault,
};

export const darkTheme = {
  ...themeColorsDark,
};

const prefixedDarkTheme = {
  ...prefixObjectKeys<typeof themeColorsDark, 'dark'>(themeColorsDark, 'dark'),
};

export const themeStyle = {
  ...defaultTheme,
  ...prefixedDarkTheme,

  // vars (colors, shadows)
  ...getCssVarObject<typeof defaultTheme>(defaultTheme),

  /**
   * Border-radius
   */
  radiusXS: '4px',
  radiusS: '6px',
  radiusM: '8px',
  radiusL: '12px',
  radiusXL: '16px',
  radiusFull: '50%',

  /**
   * Spacings
   */
  spacing4: '4px',
  spacing8: '8px',
  spacing12: '12px',
  spacing16: '16px',
  spacing24: '24px',
  spacing32: '32px',
  spacing48: '48px',
  spacing72: '72px',
  spacing96: '96px',
  spacing120: '120px',
} as const;
